// The platform fields a seller import maps onto.
// Required: Product, Length, Location. All other fields are optional.

import { UnitCode } from '~/graphql/graphql';
import type { TargetField } from './types';

/** Platform unit codes (the generated UnitCode enum). Used for the Unit dropdown and to
 * normalise pasted unit values. */
export const UNIT_CODES: string[] = Object.values(UnitCode);

export const SELLER_FIELDS: TargetField[] = [
  {
    key: 'descriptor',
    label: 'Product',
    required: true,
    type: 'descriptor',
    aliases: [
      'descriptor',
      'product',
      'product code',
      'productcode',
      'sku',
      'item',
      'description',
      'product description',
    ],
  },
  {
    key: 'length',
    label: 'Length',
    required: true,
    type: 'text',
    aliases: ['length', 'len', 'size'],
  },
  {
    key: 'locationCode',
    label: 'Location',
    required: true,
    type: 'text',
    aliases: ['location code', 'location', 'loc', 'branch', 'warehouse'],
  },
  {
    key: 'stock',
    label: 'Stock',
    required: false,
    type: 'number',
    aliases: ['stock', 'qty', 'quantity', 'on hand', 'inventory'],
  },
  {
    key: 'leadTimeDays',
    label: 'Lead Time (Days)',
    required: false,
    type: 'number',
    aliases: ['lead time (days)', 'lead time', 'leadtime', 'lead'],
  },
  {
    key: 'replenishmentRate',
    label: 'Replenishment Rate Per Week',
    required: false,
    type: 'number',
    aliases: [
      'replenishment rate per week',
      'replenishment rate',
      'replenishment',
      'replen',
    ],
  },
  {
    key: 'moq',
    label: 'Minimum Order Quantity',
    required: false,
    type: 'number',
    aliases: ['minimum order quantity', 'moq', 'min order', 'minimum'],
  },
  {
    key: 'criticalStock',
    label: 'Critical Stock Level',
    required: false,
    type: 'number',
    aliases: ['critical stock level', 'critical', 'critical level'],
  },
  {
    key: 'lowStock',
    label: 'Low Stock Level',
    required: false,
    type: 'number',
    aliases: ['low stock level', 'low', 'low level'],
  },
  {
    key: 'unit',
    label: 'Unit',
    required: false,
    type: 'text',
    aliases: ['unit', 'uom', 'units', 'unit of measure'],
    options: UNIT_CODES,
  },
];

export const FIELD_BY_KEY: Record<string, TargetField> = Object.fromEntries(
  SELLER_FIELDS.map((f) => [f.key, f])
);

export const TARGET_FIELD_KEYS = new Set<string>(
  SELLER_FIELDS.map((f) => f.key)
);

export function missingRequiredFields(mappedFieldKeys: Iterable<string>) {
  const mapped = new Set(mappedFieldKeys);
  return SELLER_FIELDS.filter(
    (field) => field.required && !mapped.has(field.key)
  );
}
